import { Navigate } from "react-big-calendar";
import dayjs from "dayjs";
import { Flex, Image, Typography } from "antd";
import { formatCompactAmount, onlyTimeFun, toCapitalize } from "../../shared";
import { can } from "../../Auth";

const { Text, Paragraph } = Typography;

const statusColors = {
  pending: "#FFA500",
  completed: "#52c41a",
  cancel: "#f5222d",
};

const CustomWeekView = ({
  date,
  events,
  onSelectEvent,
  onShowMore,
  handleShowMore,
}) => {
  const startOfWeek = dayjs(date).startOf("week");
  const endOfWeek = dayjs(date).endOf("week");
  const days = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, "day"));

  const weekEvents = events.filter((e) =>
    dayjs(e.start).isBetween(startOfWeek, endOfWeek, "day", "[]"),
  );

  const eventsByDay = days.map((day) =>
    events.filter((e) => dayjs(e.start).isSame(day, "day")),
  );

  const totalSessions = eventsByDay.reduce((sum, list) => sum + list.length, 0);

  return (
    <div className="custom-week-view">
      <div className="custom-week-header">
        <Text type="secondary">{totalSessions} sessions this week</Text>
        <a
          className="view-week-link"
          onClick={() => handleShowMore(weekEvents)}
        >
          📅 View week in drawer
        </a>
      </div>
      <div className="custom-week-grid">
        {days.map((day, i) => {
          const isToday = day.isSame(dayjs(), "day");
          const dayEvents = eventsByDay[i];
          const visible = dayEvents.slice(0, 3);
          const hidden = dayEvents.length - visible.length;

          return (
            <div key={i} className="custom-week-day">
              <div className="day-header">
                <div>
                  <div className={`dow ${isToday ? "today" : ""}`}>
                    {day.format("ddd").toUpperCase()}
                  </div>
                  <div className={`dom ${isToday ? "today" : ""}`}>
                    {day.format("D")}
                  </div>
                </div>
                {dayEvents.length > 0 && (
                  <span className="count">{dayEvents.length}</span>
                )}
              </div>

              <div className="day-events">
                {visible.map((ev) => (
                  <div
                    key={ev.id}
                    className="event-pill"
                    style={{ backgroundColor: statusColors[ev.status] }}
                    onClick={() => onSelectEvent(ev)}
                  >
                    <Flex vertical gap={2} className="w-100">
                      <Flex justify="space-between" className="w-100" gap={5}>
                        <Flex gap={2} style={{ width: "70%" }}>
                          <span className="time">{onlyTimeFun(ev.start)}</span>
                          <Paragraph
                            className="m-0 name"
                            ellipsis={{ rows: 1 }}
                            style={{ width: "max-content", textAlign: "left" }}
                          >
                            {toCapitalize(ev.patientName)}
                          </Paragraph>
                        </Flex>
                        <span className="amount" style={{ width: "30%" }}>
                          {can(
                            "Session Calendar Views",
                            "view payment details",
                          ) && (
                            <>
                              {formatCompactAmount(
                                ev.displayAmount?.toLocaleString(),
                              )}{" "}
                              (PKR)
                            </>
                          )}
                        </span>
                      </Flex>
                      <Flex justify="space-between" align="flex-start" gap={5}>
                        <Flex gap={2}>
                          <Paragraph
                            className="m-0 fs-10 text-white"
                            ellipsis={{ rows: 1 }}
                            style={{
                              width: "max-content",
                              textAlign: "left",
                              padding: "1px 7px",
                              borderRadius: 4,
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            {toCapitalize(ev?.alldata?.service?.name)}
                          </Paragraph>
                        </Flex>
                        {ev?.alldata?.appointment?.client_info?.medical_info &&
                          Object.entries(
                            ev?.alldata?.appointment?.client_info?.medical_info,
                          ).filter(
                            ([key, value]) =>
                              key.startsWith("is") && value === "1",
                          ).length > 0 && (
                            <Image
                              src={
                                ev?.status === "cancel"
                                  ? "/assets/images/issue-o.webp"
                                  : "/assets/images/issue-r.webp"
                              }
                              width={5}
                              height={"auto"}
                              preview={false}
                            />
                          )}
                      </Flex>
                    </Flex>
                  </div>
                ))}
                {hidden > 0 && (
                  <div
                    className="more-link"
                    onClick={() => onShowMore(dayEvents, day.toDate())}
                  >
                    +{hidden} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

CustomWeekView.range = (date) => {
  const start = dayjs(date).startOf("week");
  return Array.from({ length: 7 }, (_, i) => start.add(i, "day").toDate());
};

CustomWeekView.navigate = (date, action) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return dayjs(date).subtract(1, "week").toDate();
    case Navigate.NEXT:
      return dayjs(date).add(1, "week").toDate();
    default:
      return date;
  }
};

CustomWeekView.title = (date) => {
  const start = dayjs(date).startOf("week");
  const end = start.add(6, "day");
  return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
};

export { CustomWeekView };
